"use client"

import React, { useCallback, useMemo, useState } from 'react';
import Link from 'next/link';
import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/fun-ui/Button";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { Paper } from "@/stores/slices/paperSlice"
import { useFetchPapers, useStore, useSubjects } from "@/stores/appStore"
import {
  DeleteIcon,
  RenameIcon,
  RemoveIcon,
  AIQAIcon,
  BilingualIcon,
  LoaderIcon,
  MoreHorizontalIcon,
  PinIcon,
  UnpinIcon,
} from "@/components/fun-ui/icons";
import { Tip } from '@/components/fun-ui/Tip';
import ModifyPaperInfoButton from '../ModifyPaperInfoButton';
import { useRouter } from 'next/navigation';
import { PaperBadge } from '../PaperBadge';
import { MarkdownRenderer } from '@/components/papers/PaperDetail/MarkdownRenderer';

type PaperAction = {
  key: string;
  inline: React.ReactNode;
  menu: React.ReactNode;
};

type MenuConfirmAction = 'remove' | 'delete' | null;

export const PaperInformation = ({
  paper,
  subjectId,
  className = '',
  isPinned = false,
  isPinPending = false,
  pendingPinAction = null,
  isPinDisabled = false,
  pinLimit = null,
  onPinChange,
}: {
  paper: Paper,
  subjectId: string,
  className?: string;
  isPinned?: boolean;
  isPinPending?: boolean;
  pendingPinAction?: 'pin' | 'unpin' | null;
  isPinDisabled?: boolean;
  pinLimit?: number | null;
  onPinChange?: (rowHeight: number) => void;
}) => {
  const [deletePaper] = useStore((state) => [
    state.deletePaper
  ]);
  const {
    fetchPapers,
  } = useFetchPapers();
  const [paperName, setPaperName] = useState(paper.extra.title || paper.paperName);
  const { removePaper } = useSubjects();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [menuModifyOpen, setMenuModifyOpen] = useState(false);
  const [menuConfirmAction, setMenuConfirmAction] = useState<MenuConfirmAction>(null);
  const linkClassName = "h-6 p-0 text-xs leading-6 text-muted-foreground hover:text-primary hover:no-underline gap-1 [&_svg]:size-4 [&_svg]:shrink-0";
  const menuItemClassName = "flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-foreground outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground";
  const paperTitle = paper.extra.title || paper.paperName;
  const pinActionForDisplay = isPinPending && pendingPinAction ? pendingPinAction : isPinned ? 'unpin' : 'pin';
  const pinLabel = pinActionForDisplay === 'unpin' ? '取消置顶' : '置顶';
  const pinLimitTip = isPinDisabled && pinActionForDisplay === 'pin' && typeof pinLimit === 'number'
    ? `最多可置顶 ${pinLimit} 篇文献`
    : '';
  const PinActionIcon = isPinPending ? LoaderIcon : isPinned ? UnpinIcon : PinIcon;

  const onDeleteOpenChange = useCallback((closed: boolean) => {
    setDeleteOpen(closed);
    setPaperName(paper.paperName);
  }, [paper.paperName])
  const onRemoveOpenChange = useCallback((closed: boolean) => {
    setRemoveOpen(closed);
    setPaperName(paper.paperName);
  }, [paper.paperName]);
  const handleRemovePaper = useCallback(() => {
    removePaper(subjectId, [paper.paperId]);
    onRemoveOpenChange(false);
  }, [onRemoveOpenChange, paper.paperId, removePaper, subjectId]);
  const handleDeletePaper = useCallback(() => {
    deletePaper(paper.paperId, subjectId);
    onDeleteOpenChange(false);
  }, [deletePaper, onDeleteOpenChange, paper.paperId, subjectId]);
  const openMenuConfirmAction = useCallback((action: Exclude<MenuConfirmAction, null>) => {
    setMoreOpen(false);
    window.requestAnimationFrame(() => {
      setMenuConfirmAction(action);
    });
  }, []);
  const openMenuModifyDialog = useCallback(() => {
    setMoreOpen(false);
    window.requestAnimationFrame(() => {
      setMenuModifyOpen(true);
    });
  }, []);

  const router = useRouter();
  const paperActions = useMemo<PaperAction[]>(() => {
    const actions: PaperAction[] = [];
    const paperHref = subjectId ? `/subjects/${subjectId}/${paper.paperId}` : `/papers/${paper.paperId}`;

    if (!paper.titleChinese && paper.titleZh) {
      const transHref = subjectId ? `/subjects/${subjectId}/${paper.paperId}?trans=true` : `/papers/${paper.paperId}?trans=true`;
      actions.push({
        key: 'translate',
        inline: (
          <Link
            className={cn(buttonVariants({ variant: "link" }), "cursor-pointer", linkClassName)}
            target='_blank'
            href={transHref}
          >
            <BilingualIcon />
            <span>翻译</span>
          </Link>
        ),
        menu: (
          <Link className={menuItemClassName} target='_blank' href={transHref}>
            <BilingualIcon />
            翻译
          </Link>
        ),
      });
    }

    actions.push({
      key: 'qa',
      inline: (
        <Link
          className={cn(buttonVariants({ variant: "link" }), "cursor-pointer", linkClassName)}
          target='_blank'
          href={paperHref}
        >
          <AIQAIcon />
          <span>问答</span>
        </Link>
      ),
      menu: (
        <Link className={menuItemClassName} target='_blank' href={paperHref}>
          <AIQAIcon />
          问答
        </Link>
      ),
    });

    if (onPinChange) {
      const pinButton = (
        <Button
          variant="link"
          aria-disabled={isPinDisabled || isPinPending}
          className={cn(
            linkClassName,
            isPinDisabled && "cursor-not-allowed text-muted-foreground/40 hover:text-muted-foreground/40",
            isPinPending && "cursor-wait",
          )}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (isPinDisabled || isPinPending) return;
            const rowHeight = event.currentTarget.closest('tr')?.getBoundingClientRect().height ?? 0;
            onPinChange(rowHeight);
          }}
        >
          <PinActionIcon />
          <span>{pinLabel}</span>
        </Button>
      );

      actions.push({
        key: 'pin',
        inline: pinLimitTip ? (
          <Tip label={pinLimitTip} asChild disableFocusTrigger>
            <span>{pinButton}</span>
          </Tip>
        ) : pinButton,
        menu: pinLimitTip ? (
          <Tip label={pinLimitTip} asChild disableFocusTrigger>
            <span>
              <button
                type="button"
                disabled={isPinDisabled || isPinPending}
                className={cn(
                  menuItemClassName,
                  "disabled:pointer-events-none disabled:opacity-50",
                )}
                onClick={(event) => {
                  if (isPinDisabled || isPinPending) return;
                  const rowHeight = (event.currentTarget as HTMLElement | null)?.closest('tr')?.getBoundingClientRect().height ?? 0;
                  onPinChange(rowHeight);
                  setMoreOpen(false);
                }}
              >
                <PinActionIcon />
                {pinLabel}
              </button>
            </span>
          </Tip>
        ) : (
          <button
            type="button"
            disabled={isPinDisabled || isPinPending}
            className={cn(
              menuItemClassName,
              "disabled:pointer-events-none disabled:opacity-50",
            )}
            onClick={(event) => {
              if (isPinDisabled || isPinPending) return;
              const rowHeight = (event.currentTarget as HTMLElement | null)?.closest('tr')?.getBoundingClientRect().height ?? 0;
              onPinChange(rowHeight);
              setMoreOpen(false);
            }}
          >
            <PinActionIcon />
            {pinLabel}
          </button>
        ),
      });
    }

    actions.push({
      key: 'modify',
      inline: (
        <ModifyPaperInfoButton
          withLoadingLabel={!(!Object.keys(paper.extra).length && paper.extractContent > 0)}
          paperId={paper.paperId}
          asyncConfirm={async () => {
            await fetchPapers(subjectId);
          }}
        >
          <div className={cn(buttonVariants({ variant: "link" }), "cursor-pointer", linkClassName)}>
            <RenameIcon />
            <span>修正</span>
          </div>
        </ModifyPaperInfoButton>
      ),
      menu: (
        <button
          type="button"
          className={menuItemClassName}
          onClick={openMenuModifyDialog}
        >
          <RenameIcon />
          修正
        </button>
      ),
    });

    if (subjectId) {
      actions.push({
        key: 'remove',
        inline: (
          <Popover open={removeOpen} onOpenChange={onRemoveOpenChange}>
            <PopoverTrigger asChild>
              <Button variant="link" className={cn(linkClassName, "hover:text-destructive")}>
                <RemoveIcon />
                <span>移除</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <div className="grid gap-4">
                <div className="flex flex-col">
                  <p className="text-sm break-all">
                    <span className="font-bold block">移除文献</span>
                    <>确认将文献《<span className="text-destructive ">{paperName}</span>》从主题分类中移除吗？</>
                  </p>
                  <Button size="sm" variant="destructive" className="self-end" onClick={() => handleRemovePaper()}>移除</Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        ),
        menu: (
          <button
            type="button"
            className={cn(menuItemClassName, "text-destructive hover:text-destructive")}
            onClick={() => {
              openMenuConfirmAction('remove');
            }}
          >
            <RemoveIcon />
            移除
          </button>
        ),
      });
    }

    actions.push({
      key: 'delete',
      inline: (
        <Popover open={deleteOpen} onOpenChange={onDeleteOpenChange}>
          <PopoverTrigger asChild>
            <Button variant="link" className={cn(linkClassName, "hover:text-destructive")}>
              <DeleteIcon />
              <span>删除</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="grid gap-4">
              <div className="flex flex-col">
                <p className="text-sm break-all">
                  <span className="font-bold block">删除文献</span>
                  <>确认删除文献《<span className="text-destructive ">{paperName}</span>》吗？</>
                </p>
                <Button size="sm" variant="destructive" className="self-end" onClick={() => handleDeletePaper()}>删除</Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      ),
      menu: (
        <button
          type="button"
          className={cn(menuItemClassName, "text-destructive hover:text-destructive")}
          onClick={() => {
            openMenuConfirmAction('delete');
          }}
        >
          <DeleteIcon />
          删除
        </button>
      ),
    });

    return actions;
  }, [
    deleteOpen,
    fetchPapers,
    handleDeletePaper,
    handleRemovePaper,
    isPinDisabled,
    isPinPending,
    linkClassName,
    menuItemClassName,
    onDeleteOpenChange,
    onPinChange,
    onRemoveOpenChange,
    openMenuModifyDialog,
    openMenuConfirmAction,
    paper.extra,
    paper.extractContent,
    paper.paperId,
    paper.titleChinese,
    paper.titleZh,
    paperName,
    PinActionIcon,
    pinLabel,
    pinLimitTip,
    removeOpen,
    subjectId,
  ]);
  const visibleActions = paperActions.length <= 4 ? paperActions : paperActions.slice(0, 3);
  const moreActions = paperActions.length <= 4 ? [] : paperActions.slice(3);

  return (
    <div className={className}>
      <Link target='_blank' href={subjectId ? `/subjects/${subjectId}/${paper.paperId}` : `/papers/${paper.paperId}`}>
        <h4
          className="text-sm font-normal text-primary break-words space-x-1"
          title={paper.paperName ? `${paper.paperName}.pdf` : paper.paperName}
        >
          <PaperBadge className="-mt-[2px]" paper={paper} />
          <span className="hover:underline">
            <MarkdownRenderer
              className="inline"
              components={{
                p: ({ children }) => <>{children}</>,
              }}
            >
              {paperTitle}
            </MarkdownRenderer>
          </span>
        </h4>
      </Link>
      {
        (!paper.titleChinese && paper.titleZh) ? (
          <div className="mt-1 text-sm text-annotation-foreground break-all">
            <MarkdownRenderer
              className="inline"
              components={{
                p: ({ children }) => <>{children}</>,
              }}
            >
              {paper.titleZh}
            </MarkdownRenderer>
          </div>
        ) : null
      }
      {
        !subjectId && paper.subjects.length ? (
          <div className="flex text-annotation-foreground text-xs overflow-hidden space-x-1 mt-2.5">
            {
              paper.subjects.slice(0, 2).map(({ subjectName, id }) => {
                return (
                  <Tip
                    key={`${paper.paperId}-${id}`}
                    label=""
                    side="bottom"
                    sideOffset={4}
                  >
                    <span
                      onClick={() => {
                        router.push(`/subjects/${id}`);
                      }}
                      className="flex-1 rounded-full px-2 border border-annotation-foreground truncate w-[80px] max-w-fit cursor-pointer hover:border-primary hover:text-primary"
                    >{subjectName}</span>
                  </Tip>
                )
              })
            }
            {
              paper.subjects.slice(2).length ? (
                <Tip
                  label={paper.subjects.slice(2).map(({ subjectName }) => subjectName).join('、')}
                  className="flex-none rounded-full px-2 border border-annotation-foreground min-w-[30px] cursor-pointer"
                  side="bottom"
                  align="start"
                  sideOffset={4}
                >
                  <span
                    style={{
                      letterSpacing: "-0.2em"
                    }}
                  >···&nbsp;</span>
                </Tip>
              ) : null
            }
          </div>
        ) : null
      }
      <div className="group/paper-actions flex items-center justify-start gap-x-3 gap-y-4 mt-5 mb-2 whitespace-nowrap flex-wrap">
        {visibleActions.map((action) => (
          <React.Fragment key={action.key}>{action.inline}</React.Fragment>
        ))}
        {moreActions.length ? (
          <HoverCard
            open={moreOpen && menuConfirmAction === null}
            onOpenChange={setMoreOpen}
            openDelay={0}
            closeDelay={120}
          >
            <HoverCardTrigger asChild>
              <Button
                variant="link"
                className={cn(
                  linkClassName,
                  "w-6 p-1 -ml-1 rounded-md hover:bg-muted",
                )}
                aria-label="更多操作"
              >
                <MoreHorizontalIcon />
                <span className="sr-only">更多操作</span>
              </Button>
            </HoverCardTrigger>
            <HoverCardContent
              align="start"
              sideOffset={0}
              className="w-[120px] p-1"
            >
              {moreActions.map((action) => (
                <React.Fragment key={action.key}>{action.menu}</React.Fragment>
              ))}
            </HoverCardContent>
          </HoverCard>
        ) : null}
      </div>
      <AlertDialog
        open={menuConfirmAction === 'remove'}
        onOpenChange={(open) => {
          if (!open) setMenuConfirmAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>移除文献</AlertDialogTitle>
            <AlertDialogDescription className="mt-4">
              确认将文献《<span className="text-destructive ">{paperName}</span>》从主题分类中移除吗？
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="outline-none focus-visible:ring-0 cursor-pointer shadow-none h-10">取消</AlertDialogCancel>
            <Button
              variant="destructive"
              className="outline-none focus-visible:ring-0 cursor-pointer"
              onClick={() => {
                handleRemovePaper();
                setMenuConfirmAction(null);
              }}
            >
              移除
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ModifyPaperInfoButton
        open={menuModifyOpen}
        onOpenChange={setMenuModifyOpen}
        withLoadingLabel={!(!Object.keys(paper.extra).length && paper.extractContent > 0)}
        paperId={paper.paperId}
        asyncConfirm={async () => {
          await fetchPapers(subjectId);
        }}
      >
        <span />
      </ModifyPaperInfoButton>
      <AlertDialog
        open={menuConfirmAction === 'delete'}
        onOpenChange={(open) => {
          if (!open) setMenuConfirmAction(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>删除文献</AlertDialogTitle>
            <AlertDialogDescription className="mt-4">
              确认删除文献《<span className="text-destructive ">{paperName}</span>》吗？
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="outline-none focus-visible:ring-0 cursor-pointer shadow-none h-10">取消</AlertDialogCancel>
            <Button
              variant="destructive"
              className="outline-none focus-visible:ring-0 cursor-pointer"
              onClick={() => {
                handleDeletePaper();
                setMenuConfirmAction(null);
              }}
            >
              删除
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
